import {
  generateReportFailure,
  generateReportSuccess,
  getDataPageRequest,
  getTransactionsFilters,
  loadReportSuccess,
} from '../actions';
import { call, put, select, take, fork, cancel, race } from 'redux-saga/effects';
import { StoreState } from 'store/types';
import { getVatReportData } from '../selectors';
import { ApiError } from 'store/api/types';
import { LoadReportRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import { LoadReportResponse, loadReport } from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { VatReportData } from '../models';
import { logoutRequest } from 'store/auth/actions';
import { push } from 'store/navigation/actions';
import routes from 'routes';
import { renderErrorMessage } from 'utils/render-error-message';
import { SET_CURRENT_JOURNALS_PAGE } from '../constants';
import { JournalType } from '../models/vatReportData';
import { CANCEL_VAT_SAGA, LOAD_REPORT_REQUEST } from '../constants';
import i18n from 'i18next';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* loadReportSaga(action: LoadReportRequestAction) {
  try {
    let err: ApiError | null;
    const {
      vatLoadRequestData,
      reportId,
      prefetchPageSize,
      prefetchTransactionsPageSize,
      prefetchExceptionsPageSize,
      skipJournalPages,
    } = action.payload;
    const transactionsPageSize =
      typeof prefetchTransactionsPageSize === 'number' &&
      prefetchTransactionsPageSize > 0
        ? prefetchTransactionsPageSize
        : typeof prefetchPageSize === 'number' && prefetchPageSize > 0
          ? prefetchPageSize
          : 100;
    const exceptionsPageSize =
      typeof prefetchExceptionsPageSize === 'number' && prefetchExceptionsPageSize > 0
        ? prefetchExceptionsPageSize
        : transactionsPageSize;
    let noDataMessage = '';
    let lastCashAccountingId = 1;
    let lastAccrualsId = 1;
    let isEmpty = false;

    do {
      const isFirstPage = lastCashAccountingId === 1 && lastAccrualsId === 1;
      const response: LoadReportResponse = yield call(
        loadReport,
        vatLoadRequestData,
        lastAccrualsId,
        lastCashAccountingId,
        reportId
      );

      err = checkResponse(response?.responseMessage);
      if (err) throw err;

      /* if (
        !response.data &&
        response.responseMessage.messageType === MessageType.WARNING
      ) {
        noDataMessage = response.responseMessage.message;
        isEmpty = true;
      } else  */
      if (
        !response.data &&
        response.responseMessage.message === 'Loaded from cached report'
      ) {
        isEmpty = true;
        yield put(loadReportSuccess());
      } else {
        const vatReportData: VatReportData =
          Object.keys(response.data).length > 0
            ? response.data
            : ({
                empty: true,
              } as VatReportData);

        if (vatReportData.empty || vatReportData.recap) {
          isEmpty = true;

          if (!vatReportData.journals) {
            vatReportData.journals = [];
          }

          if (!vatReportData.exceptions) {
            vatReportData.exceptions = [];
          }

          vatReportData.totalPages = 0;
        } else {
          vatReportData.totalPages = 1;
        }

        lastAccrualsId = vatReportData.accrualsLastId ?? lastAccrualsId;
        lastCashAccountingId =
          vatReportData.cashAccountingLastId ?? lastCashAccountingId;
        yield put(generateReportSuccess(vatReportData, isEmpty, isFirstPage));
      }
    } while (!isEmpty);
    if (noDataMessage) {
      const message: MessageData = {
        title: i18n.t('notifications.server-message-2'),
        description: noDataMessage,
        type: MessageStates.WARNING,
      };
      yield put(turnMessageOn(message));
      yield put(generateReportFailure());
      yield put(push(routes.main));
    } else if (!skipJournalPages) {
      //fetch filters
      yield put(
        getTransactionsFilters(
          vatLoadRequestData?.provider,
          vatLoadRequestData?.connectionId,
          vatLoadRequestData?.mappingId
        )
      ); //to fix for qbo
      const loadedReportData: ReturnType<typeof getVatReportData> = yield select(
        (state: StoreState) => getVatReportData(state)
      );
      const useSummarized = (loadedReportData?.summarizedSize ?? 0) > 0;
      //fetch first page — match generate/refresh (summarised when available)
      yield put(
        getDataPageRequest(
          reportId,
          1,
          transactionsPageSize,
          JournalType.TRANSACTION,
          useSummarized ? 'summarised' : 'detailed'
        )
      );
      yield take(SET_CURRENT_JOURNALS_PAGE);
      yield put(
        getDataPageRequest(
          reportId,
          1,
          exceptionsPageSize,
          JournalType.EXCEPTION,
          'detailed'
        )
      );
    }
  } catch (error: any) {
    const { vatLoadRequestData } = action.payload;
    const errorWithContext = {
      ...error,
      connectionId: vatLoadRequestData?.connectionId,
      mappingId: vatLoadRequestData?.mappingId,
    };
    const message: MessageData | null = renderErrorMessage(errorWithContext);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(generateReportFailure());
  }
}

export function* watchLoadReportSaga() {
  let task;
  while (true) {
    const action = yield take(LOAD_REPORT_REQUEST);
    if (task) {
      yield cancel(task);
    }
    task = yield fork(loadReportSaga, action);

    // Race between saga completion and cancellation
    const result = yield race({
      completed: task,
      cancelled: take(CANCEL_VAT_SAGA),
    });

    if (result.cancelled) {
      yield cancel(task);
    }
  }
}
